import { lazy } from "react";

import { NotFoundView } from "views/not-found";
import { VerificationTemplatesView } from "views/verification-templates";
import { VerificationTemplatesIdView } from "views/verification-templates-id";
import { ApplicationsIdReportView } from "views/applications-id-report";
import { ApplicationsIdOverview } from "views/applications-id";
import { AddPropertyPage } from "views/properties-add";
import { PropertyDetail } from "views/properties-id";
import { Properties } from "views/properties";
import { Units } from "views/units";
import { UnitDetail } from "views/properties-id-units-id";
import { AddUnitsView } from "views/properties-id-units-add";
import { RentReportingView } from "views/rent-reporting";
import { RentReportingUpload } from "views/rent-reporting-upload";
import { AddNewSubscriber } from "views/rent-reporting-add";
import { SubscriptionDetails } from "views/rent-reporting-id";
import { EditSubscriptionDetails } from "views/rent-reporting-id-edit";
import { RentersSettingsView } from "views/rent-reporting-settings";
import { RentReportingUploadIdView } from "views/rent-reporting-upload-id";
import { RentReportingCsvAnalysisView } from "views/rent-reporting-upload-csv-analysis";
import { ResubscribeSubscriptionDetails } from "views/rent-reporting-id-resubscribe";
import { IdentityTheftProtectionView } from "views/identity-theft-protection";
import { PropertyGroups } from "views/property-groups";
import { PropertyGroupsConfiguration } from "views/property-groups-configuration";
import { PropertyGroupDetail } from "views/property-groups-id";
import { ApplicationsIdReportsPrintReceipt } from "views/applications-id-reports-print-receipt";
import { BoomNotificationView } from "views/boom-notifications";
import { DiscountsRouteWrapper } from "providers/Routes/components/DiscountsRouteWrapper";
import { LandlordSurveysView } from "views/landlord-surveys";
import { LandlordSurveysIdView } from "views/landlord-surveys-id";
import { ApplicationsView } from "views/applications";
import type { PartnerUserPermissions } from "providers/Auth";

import type { RouteConfig } from "../types";

const RentersPage = lazy(() => import("views/renters"));
const ChatsView = lazy(() => import("views/chats"));
const InboxView = lazy(() => import("views/inbox"));
const CreditImpactView = lazy(() => import("views/credit-impact"));
const AnalyticsView = lazy(() => import("views/analytics"));
const TaskView = lazy(() => import("views/tasks"));
const LeadsIdView = lazy(() => import("views/leads-id"));
const LeadsView = lazy(() => import("views/leads"));
const OutcomesView = lazy(() => import("views/outcomes"));
const ContactsView = lazy(() => import("views/contacts"));
const ContactsIdView = lazy(() => import("views/contacts-id"));
const ForbiddenView = lazy(() => import("views/forbidden"));
const WorkflowsView = lazy(() => import("views/workflows"));
const WorkflowDetail = lazy(() => import("views/workflows-id"));
const ListingSettingsView = lazy(() => import("views/listing-settings"));
const ListingTemplatesIdView = lazy(() => import("views/listing-settings-id"));
const ShowingsView = lazy(() => import("views/showings"));
const LockboxesView = lazy(() => import("views/lockboxes"));
const PhoneNumbersView = lazy(() => import("views/phone-numbers"));

type PartnerType = {
  isBoomScreen: boolean;
  isBoomCRM: boolean;
  isNativeChatActive: boolean;
  isCommunicationSms: boolean;
  isBoomScreenOutcomes: boolean;
  isProvisionedPhoneNumber: boolean;
  isBoomOps: boolean;
  isTasks: boolean;
  isLockboxes: boolean;
  isShowings: boolean;
  isCrmListingSync: boolean;
  adminPrivileges: boolean;
};

export const getMainRoutes = (
  type: PartnerType,
  permissions: PartnerUserPermissions
): RouteConfig[] => [
  // BoomScreen — legacy Stream chat is hidden for everyone since the native
  // chat migration; the view itself gets removed with the Stream cleanup.
  { path: "chats", component: ChatsView, when: false },

  // BoomCRM — native chat lives in the Inbox and ships to BoomScreen-only
  // partners too, for as long as chat stays switched on (see
  // `isNativeChatActive`). Boom ops also keep Inbox access to verify migrated
  // chat.
  {
    path: "inbox",
    component: InboxView,
    when:
      type.isBoomCRM ||
      (type.isNativeChatActive && type.isBoomScreen) ||
      type.isBoomOps
  },
  { path: "leads", component: LeadsView, when: type.isBoomCRM },
  { path: "leads/:leadId", component: LeadsIdView, when: type.isBoomCRM },
  { path: "contacts", component: ContactsView, when: type.isBoomCRM },
  {
    path: "contacts/:contactId",
    component: ContactsIdView,
    when: type.isBoomCRM
  },
  { path: "tasks", component: TaskView, when: type.isBoomCRM && type.isTasks },
  {
    path: "listing-settings",
    component: ListingSettingsView,
    when: type.isBoomCRM
  },
  {
    path: "listing-settings/:id",
    component: ListingTemplatesIdView,
    when: type.isBoomCRM
  },
  {
    path: "lockboxes",
    component: LockboxesView,
    when: type.isBoomCRM && type.isLockboxes
  },
  {
    path: "showings",
    component: ShowingsView,
    when: type.isBoomCRM && type.isShowings
  },
  {
    path: "phone-numbers",
    component: PhoneNumbersView,
    when: type.isBoomCRM && type.isProvisionedPhoneNumber
  },

  // Renters & Rent Reporting
  { path: "renters", component: RentersPage },
  { path: "rent-reporting/settings", component: RentersSettingsView },
  { path: "credit-impact", component: CreditImpactView },
  { path: "rent-reporting", component: RentReportingView },
  {
    path: "identity-theft-protection",
    component: IdentityTheftProtectionView
  },
  { path: "rent-reporting/:renterId", component: SubscriptionDetails },
  { path: "rent-reporting/upload", component: RentReportingUpload },
  {
    path: "rent-reporting/upload/csv-analysis",
    component: RentReportingCsvAnalysisView
  },
  { path: "rent-reporting/upload/:id", component: RentReportingUploadIdView },
  { path: "rent-reporting/add", component: AddNewSubscriber },
  {
    path: "rent-reporting/:renterId/edit",
    component: EditSubscriptionDetails
  },
  {
    path: "rent-reporting/:renterId/resubscribe",
    component: ResubscribeSubscriptionDetails
  },
  { path: "notifications", component: BoomNotificationView },

  // Properties
  { path: "properties", component: Properties },
  { path: "properties/add", component: AddPropertyPage },
  { path: "properties/:propertyId", component: PropertyDetail },
  { path: "properties/:propertyId/units/add", component: AddUnitsView },
  { path: "properties/:propertyId/units/:unitId", component: UnitDetail },
  { path: "units", component: Units },
  { path: "property-groups", component: PropertyGroups },
  { path: "property-groups/:id", component: PropertyGroupDetail },
  {
    path: "property-groups/configuration",
    component: PropertyGroupsConfiguration
  },

  // Applications
  { path: "applications", component: ApplicationsView },
  {
    path: "outcomes",
    component: OutcomesView,
    when: type.isBoomScreen && type.isBoomScreenOutcomes && type.adminPrivileges
  },
  { path: "applications/:applicationId", component: ApplicationsIdOverview },
  {
    path: "applications/:applicationId/reports",
    component: ApplicationsIdReportView,
    when: permissions.canViewReports
  },
  {
    path: "applications/:applicationId/reports/print/receipt",
    component: ApplicationsIdReportsPrintReceipt
  },

  // Workflows
  { path: "workflows", component: WorkflowsView },
  { path: "workflows/:workflowId", component: WorkflowDetail },

  // Verification & Discounts
  {
    path: "verification-templates",
    component: VerificationTemplatesView,
    when: permissions.canAccessScreeningTemplates
  },
  {
    path: "verification-templates/:id",
    component: VerificationTemplatesIdView,
    when: permissions.canAccessScreeningTemplates
  },
  {
    path: "discounts",
    component: DiscountsRouteWrapper,
    when: permissions.canManageDiscounts
  },

  // Surveys
  { path: "landlord-surveys", component: LandlordSurveysView },
  { path: "landlord-surveys/:id", component: LandlordSurveysIdView },

  // Analytics — always available; feature flags gate the subtabs inside the view
  { path: "analytics", component: AnalyticsView },

  // Error pages
  { path: "forbidden", component: ForbiddenView },
  { path: "*", component: NotFoundView }
];
